import { ScientificAnalysisConditionComponent, ScientificAnalysisStaticImplements } from '@/modules/scientific-analysis-module/components/scientific-analysis-filter-panel/scientific-analysis-filter-panel-conditionfactory'
import { ScientificAnalysisComparatorType, ScientificAnalysisConditionTypeEnum } from '@/modules/scientific-analysis-module/services/scientific-analysis-dtos'
import { ScientificAnalysisComparisonType, ScientificAnalysisCondition, ScientificAnalysisConditionValidationErrorCode } from '@/modules/scientific-analysis-module/shared/scientific-analysis.models'
import moment, { Moment } from 'moment'
import { DateUtils } from '@/shared/util/date-utils'
import { Vue, Component, Prop, Emit } from 'vue-property-decorator'

@Component
// eslint-disable-next-line no-use-before-define
@ScientificAnalysisStaticImplements<ScientificAnalysisConditionComponent<ScientificAnalysisFilterPanelRange>>()
export default class ScientificAnalysisFilterPanelRange extends Vue {
    static COMPONENT_NAME = 'scientific-analysis-filter-panel-range'
    private static INPUT_TYPE_TEXT = 'text'
    private static INPUT_TYPE_NUMBER = 'number'
    private static DATE_TYPE_DATE = 'date'
    private static DATE_TYPE_DATETIME = 'datetime'

    @Prop() condition!: ScientificAnalysisCondition

    static canAccept(comparisonType: ScientificAnalysisComparisonType): boolean {
        return (
            !comparisonType.additionalInfo ||
            comparisonType.additionalInfo === null ||
            comparisonType.additionalInfo.length === 0
        ) &&
            comparisonType.comparatorType === ScientificAnalysisComparatorType.IN_RANGE
    }

    get basicInputType(): string {
        if (this.isBasicComparison) {
            if (this.isTextComparison) {
                return ScientificAnalysisFilterPanelRange.INPUT_TYPE_TEXT
            } else {
                return ScientificAnalysisFilterPanelRange.INPUT_TYPE_NUMBER
            }
        } else {
            return ''
        }
    }

    get dateType(): string {
        if (this.isDateTimeComparison) {
            return ScientificAnalysisFilterPanelRange.DATE_TYPE_DATETIME
        } else {
            return ScientificAnalysisFilterPanelRange.DATE_TYPE_DATE
        }
    }

    get datePattern(): string {
        if (this.isDateTimeComparison) {
            return DateUtils.localeDateTimePattern
        } else {
            return DateUtils.localeDatePattern
        }
    }

    get isBasicComparison(): boolean {
        return this.isNumberComparison || this.isTextComparison
    }

    get isDateComparison(): boolean {
        return this.condition.comparisonType.conditionType === ScientificAnalysisConditionTypeEnum.DATE || this.isDateTimeComparison
    }
    get isDateTimeComparison(): boolean {
        return this.condition.comparisonType.conditionType === ScientificAnalysisConditionTypeEnum.DATETIME
    }
    get isNumberComparison(): boolean {
        return this.condition.comparisonType.conditionType === ScientificAnalysisConditionTypeEnum.NUMBER
    }
    get isTextComparison(): boolean {
        return this.condition.comparisonType.conditionType === ScientificAnalysisConditionTypeEnum.TEXT
    }

    private getFormattedDate(date: Moment | null):string {
        return moment(date).format(this.isDateTimeComparison ? DateUtils.zonedDateTimePattern : DateUtils.datePattern)
    }

    onDateFrom(newDate: Moment | null): void {
        if (newDate) {
            this.condition.value = this.getFormattedDate(newDate)
        } else {
            this.condition.value = ''
        }
        this._onChange()
    }

    onDateTo(newDate: Moment | null):void {
        if (newDate) {
            this.condition.valueTo = this.getFormattedDate(newDate)
        } else {
            this.condition.valueTo = ''
        }
        this._onChange()
    }

    private validateCondition():void {
        if (this.condition.value.length === 0 || this.condition.valueTo!.length === 0) {
            this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.EMPTY
        } else if (this.isDateComparison) {
            const from = moment(this.condition.value)
            const to = moment(this.condition.valueTo)
            if (!from.isValid() || !to.isValid()) {
                this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.EMPTY
            } else if (from.isAfter(to)) {
                this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.FROM_GREATERTHAN_TO
            } else {
                this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.NONE
            }
        } else if (this.isNumberComparison) {
            const from = parseFloat(this.condition.value as string)
            const to = parseFloat(this.condition.valueTo as string ?? '0')
            if (from >= to) {
                this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.FROM_GREATERTHAN_TO
            } else {
                this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.NONE
            }
        } else {
            this.condition.validationErrorCode = ScientificAnalysisConditionValidationErrorCode.NONE
        }
    }

    get _validateFrom(): boolean {
        this.validateCondition()
        const from = moment(this.condition.value)
        return !from.isValid() ||
            this.condition.value.length === 0 ||
            this.condition.validationErrorCode === ScientificAnalysisConditionValidationErrorCode.FROM_GREATERTHAN_TO
    }

    get _validateTo(): boolean {
        this.validateCondition()
        const to = moment(this.condition.valueTo)
        return !to.isValid() ||
            this.condition.valueTo?.length === 0 ||
            this.condition.validationErrorCode === ScientificAnalysisConditionValidationErrorCode.FROM_GREATERTHAN_TO
    }

    @Emit('modification')
    _onChange():void { }
}
